<template>
  <v-container>
    <v-row
      align="center"
    >
      <v-col
        cols="auto"
      >
        <div class="title text-uppercase font-weight-medium">
          Browse Radios
        </div>
      </v-col>
      <v-spacer />
      <v-col cols="auto">
        <v-text-field
          v-model="filters.name"
          solo
          placeholder="Search"
          flat
          dense
          hide-details
          clearable
          append-icon="fas fa-search"
        />
      </v-col>
      <v-col cols="auto">
        <v-btn
          dark
          color="primary"
          class="ma-0"
          @click="editRadioDialog.radio = undefined; editRadioDialog.visible = true"
        >
          <v-icon
            left
            small
          >
            fas fa-plus
          </v-icon>
          Add New
        </v-btn>
      </v-col>
    </v-row>

    <v-row>
      <!--<editor-fold desc="Filters">-->
      <filters-column
        :filters-active="filtersActive"
        @filters:clear-all="clearFilters()"
      >
        <v-subheader class="font-weight-medium">
          Client
        </v-subheader>
        <v-autocomplete
          v-model="filters.client_id"
          solo
          flat
          outlined
          :items="clients.data"
          :loading="clients.loading"
          clearable
          item-text="name"
          item-value="id"
          placeholder="Select client..."
          dense
        />
      </filters-column>
      <!--</editor-fold>-->

      <!--<editor-fold desc="Table">-->
      <v-col>
        <pagination
          :page.sync="items.pagination.page"
          :last-page="items.meta.last_page"
          :total="items.meta.total"
          :from="items.meta.from"
          :to="items.meta.to"
        />
        <v-data-table
          :headers="headers"
          :items="items.data"
          :server-items-length="items.meta.total"
          :sort-by.sync="items.sorting.sortBy"
          :sort-desc.sync="items.sorting.desc"
          :page.sync="items.pagination.page"
          :items-per-page="items.pagination.perPage"
          :loading="items.loading"
          hide-default-footer
          must-sort
          class="v-data-table--striped elevation-15"
          @update:sort-by="fetchItems()"
          @update:page="fetchItems()"
        >
          <template v-slot:item="{ item: radio }">
            <tr>
              <td>
                <router-link :to="{ name: 'radio', params: { id: radio.id }}">
                  <div class="subtitle-2 primary--text">
                    {{ radio.title }}
                  </div>
                  <div class="caption text--secondary">
                    {{ radio.description }}
                  </div>
                </router-link>
              </td>
              <td>
                <router-link :to="{ name: 'radio', params: { id: radio.id }}">
                  <span v-if="radio.client">
                    {{ radio.client.name }}
                  </span>
                  <span
                    v-else
                    class="grey--text"
                  >
                    None
                  </span>
                </router-link>
              </td>
              <td
                style="width: 1px"
                class="text-no-wrap"
              >
                <router-link :to="{ name: 'radio', params: { id: radio.id }}">
                  {{ radio.size }} GB
                </router-link>
              </td>
              <td>
                <router-link :to="{ name: 'radio', params: { id: radio.id }}">
                  <span v-if="radio.updated_at">{{ $dayjs(radio.updated_at).format('DD.MM.YYYY. HH:mm:ss') }}</span>
                  <span
                    v-else
                    class="grey--text text--lighten-1"
                  >
                    Never
                  </span>
                </router-link>
              </td>
              <td>
                <router-link :to="{ name: 'radio', params: { id: radio.id }}">
                  <span
                    v-if="radio.last_sync_at"
                    :class="{ 'error--text': $dayjs(radio.last_sync_at).isBefore($dayjs(radio.updated_at)) }"
                  >
                    {{ $dayjs(radio.last_sync_at).format('DD.MM.YYYY. HH:mm:ss') }}
                  </span>
                  <span
                    v-else
                    class="grey--text text--lighten-1"
                  >
                    Never
                  </span>
                </router-link>
              </td>
              <td
                class="text-no-wrap"
                style="width: 1px"
              >
                <v-menu
                  offset-y
                  min-width="100px"
                >
                  <template v-slot:activator="{ on }">
                    <v-btn
                      icon
                      color="grey"
                      small
                      v-on="on"
                    >
                      <v-icon small>
                        fa fa-ellipsis-v
                      </v-icon>
                    </v-btn>
                  </template>
                  <v-list
                    dense
                    class="body-2"
                  >
                    <v-list-item :to="{ name: 'radio', params: { id: radio.id }}">
                      <v-list-item-icon>
                        <v-icon small>
                          fa fa-arrow-right
                        </v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>View Radio Details</v-list-item-content>
                    </v-list-item>
                    <v-list-item @click="editRadioDialog.radio = radio; editRadioDialog.visible = true">
                      <v-list-item-icon>
                        <v-icon small>
                          fa fa-pen
                        </v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>Edit Radio</v-list-item-content>
                    </v-list-item>
                    <v-divider />
                    <v-list-item @click="deleteDialog.radio = radio; deleteDialog.visible = true">
                      <v-list-item-icon>
                        <v-icon small>
                          fa fa-trash
                        </v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>Delete Radio</v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </td>
            </tr>
            <template v-if="radio.children && radio.children.length > 0">
              <tr
                v-for="childRadio in radio.children"
                :key="childRadio.id"
              >
                <td>
                  <router-link :to="{ name: 'radio', params: { id: childRadio.id }}">
                    <v-row no-gutters>
                      <v-col
                        cols="auto"
                        class="pl-1 pr-3"
                      >
                        <v-icon>
                          fas fa-level-up-alt fa-rotate-90
                        </v-icon>
                      </v-col>
                      <v-col>
                        <div class="subtitle-2 primary--text">
                          {{ childRadio.title }}
                        </div>
                        <div class="caption text--secondary">
                          {{ childRadio.description }}
                        </div>
                      </v-col>
                    </v-row>
                  </router-link>
                </td>
                <td>
                  <router-link :to="{ name: 'radio', params: { id: childRadio.id }}">
                    <span v-if="childRadio.client">
                      {{ childRadio.client.name }}
                    </span>
                    <span
                      v-else
                      class="grey--text"
                    >
                      None
                    </span>
                  </router-link>
                </td>
                <td
                  style="width: 1px"
                  class="text-no-wrap"
                >
                  <router-link :to="{ name: 'radio', params: { id: childRadio.id }}">
                    {{ childRadio.size }} GB
                  </router-link>
                </td>
                <td>
                  <router-link :to="{ name: 'radio', params: { id: childRadio.id }}">
                    <span v-if="childRadio.updated_at">{{ $dayjs(childRadio.updated_at).format('DD.MM.YYYY. HH:mm:ss') }}</span>
                    <span
                      v-else
                      class="grey--text text--lighten-1"
                    >
                      Never
                    </span>
                  </router-link>
                </td>
                <td>
                  <router-link :to="{ name: 'radio', params: { id: childRadio.id }}">
                    <span v-if="childRadio.last_sync_at">{{ $dayjs(childRadio.last_sync_at).format('DD.MM.YYYY. HH:mm:ss') }}</span>
                    <span
                      v-else
                      class="grey--text text--lighten-1"
                    >
                      Never
                    </span>
                  </router-link>
                </td>
                <td
                  class="text-no-wrap"
                  style="width: 1px"
                >
                  <v-menu
                    offset-y
                    min-width="100px"
                  >
                    <template v-slot:activator="{ on }">
                      <v-btn
                        icon
                        color="grey"
                        small
                        v-on="on"
                      >
                        <v-icon small>
                          fa fa-ellipsis-v
                        </v-icon>
                      </v-btn>
                    </template>
                    <v-list
                      dense
                      class="body-2"
                    >
                      <v-list-item :to="{ name: 'radio', params: { id: childRadio.id }}">
                        <v-list-item-icon>
                          <v-icon small>
                            fa fa-arrow-right
                          </v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>View Radio Details</v-list-item-content>
                      </v-list-item>
                      <v-list-item @click="editRadioDialog.radio = childRadio; editRadioDialog.visible = true">
                        <v-list-item-icon>
                          <v-icon small>
                            fa fa-pen
                          </v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>Edit Radio</v-list-item-content>
                      </v-list-item>
                      <v-divider />
                      <v-list-item @click="deleteDialog.radio = childRadio; deleteDialog.visible = true">
                        <v-list-item-icon>
                          <v-icon small>
                            fa fa-trash
                          </v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>Delete Radio</v-list-item-content>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </td>
              </tr>
            </template>
          </template>
        </v-data-table>
        <div class="pa-2">
          <pagination
            :page.sync="items.pagination.page"
            :last-page="items.meta.last_page"
            :total="items.meta.total"
            :from="items.meta.from"
            :to="items.meta.to"
          />
        </div>
      </v-col>
    </v-row>

    <edit-radio-dialog
      :visible.sync="editRadioDialog.visible"
      :radio="editRadioDialog.radio"
      @radio:updated="fetchItems()"
      @radio:created="fetchItems()"
      @radio:deleted="fetchItems()"
    />

    <confirm-delete-dialog
      :visible.sync="deleteDialog.visible"
      :submitting="deleteDialog.deleting"
      @confirm="deleteRadio()"
    />
  </v-container>
</template>

<script>
import fetchableItemsMixin from '@/mixins/fetchableItemsMixin'
import Radios from '@/services/Radios'
import FiltersColumn from '@/components/helpers/FiltersColumn'
import Clients from '@/services/Clients'
import { cloneDeep } from 'lodash'
import RadioModel from '@/models/RadioModel'
import EditRadioDialog from '@/components/radio/EditRadioDialog'
import ConfirmDeleteDialog from '@/components/helpers/ConfirmDeleteDialog'

export default {
  name: 'Radios',
  components: {
    ConfirmDeleteDialog,
    EditRadioDialog,
    FiltersColumn
  },
  mixins: [fetchableItemsMixin],
  data() {
    return {
      editRadioDialog: {
        visible: false,
        radio: undefined
      },
      deleteDialog: {
        radio: undefined,
        visible: false,
        deleting: false
      },
      clients: {
        loading: false,
        data: []
      }
    }
  },
  beforeCreate() {
    this.emptyFilters = cloneDeep(RadioModel.emptyFilters)
  },
  created() {
    this.headers = [
      { text: 'Name', value: 'title' },
      { text: 'Client', value: 'client_name' },
      { text: 'Size', value: 'size' },
      { text: 'Last Change', value: 'updated_at' },
      { text: 'Last Sync', value: 'last_sync_at' },
      { sortable: false }
    ]

    this.fetchClients()
  },
  methods: {
    async itemsFetcher(queryParams) {
      return await Radios.fetch(queryParams)
    },
    async fetchClients() {
      this.clients.loading = true

      try {
        this.clients.data = (await Clients.fetch())
      } catch (e) {

      } finally {
        this.clients.loading = false
      }
    },
    async deleteRadio() {
      if (!this.deleteDialog.radio) return

      this.deleteDialog.deleting = true

      try {
        await Radios.delete(this.deleteDialog.radio.id)

        this.$store.dispatch('alert', {
          text: 'Successfully deleted',
          color: 'success'
        })

        this.fetchItems()
      } catch (e) {
        this.$store.dispatch('alert', {
          text: 'Something went wrong',
          color: 'error'
        })
        throw e
      } finally {
        this.deleteDialog.visible = false
        this.deleteDialog.deleting = false
      }
    }
  }
}
</script>
